import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

type Severity = 'critical' | 'high' | 'medium' | 'low';
type FindingKind = 'vulnerability' | 'hotspot';

/** One entry from GET /api/sysdevops/security/vulnerabilities. */
interface SecurityFinding {
  id: string;
  kind: FindingKind;
  project: string;
  severity: Severity;
  title: string;
  rule: string | null;
  file: string;
  line: number | null;
  status: 'open' | 'to_review';
  discovered: string;
  url: string;
}

/** GET /api/sysdevops/security/metrics */
interface SecuritySummary {
  // SonarQube's A (best) to E, the worst across projects; null when unrated.
  securityRating: 'A' | 'B' | 'C' | 'D' | 'E' | null;
  openVulnerabilities: Record<Severity, number> & { total: number };
  hotspotsToReview: number;
  projects: string[];
  lastAnalysis: string | null;
}

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];

const SEVERITY_STYLES: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const KIND_LABELS: Record<FindingKind, string> = {
  vulnerability: 'Vulnerability',
  hotspot: 'Hotspot to review',
};

const ratingColor = (rating: SecuritySummary['securityRating']) => {
  if (rating === 'A') return 'text-scarf-green';
  if (rating === 'B') return 'text-brand-blue-light';
  if (rating) return 'text-brand-red';
  return 'text-gray-400';
};

/**
 * Security findings, from SonarQube.
 *
 * This tab used to show invented vulnerabilities from its API ("Package xyz
 * is outdated") and read counts the API never sent, so its totals were
 * blank. It had a Run Security Scan button whose endpoint started nothing.
 * It now shows SonarQube's open vulnerabilities and the security hotspots
 * still to review, each linked to SonarQube. SonarQube analyses on every
 * push, so there is no scan to start here.
 */
const SecurityComponent: React.FC = () => {
  const [findings, setFindings] = useState<SecurityFinding[]>([]);
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const [selectedKind, setSelectedKind] = useState<FindingKind | 'all'>('all');

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [findingsResponse, summaryResponse] = await Promise.all([
        apiService.getSecurityVulnerabilities(),
        apiService.getSecurityMetrics(),
      ]);
      const findingsData = findingsResponse.data as { findings?: SecurityFinding[] } | undefined;
      setFindings(findingsData?.findings ?? []);
      setSummary((summaryResponse.data as SecuritySummary | undefined) ?? null);
    } catch (err) {
      // The server's own reason, e.g. "SonarQube is unavailable: …".
      setError(err instanceof Error ? err.message : 'Could not reach the server to load security data');
      setFindings([]);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const visibleFindings = findings.filter((finding) => (
    (selectedSeverity === 'all' || finding.severity === selectedSeverity)
    && (selectedKind === 'all' || finding.kind === selectedKind)
  ));

  if (isLoading && !summary && !error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600 text-lg">Loading security data…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
          {error}
        </div>
      )}

      {summary && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Security Rating</h3>
            <div className={`text-3xl font-bold ${ratingColor(summary.securityRating)}`}>
              {summary.securityRating ?? '–'}
            </div>
            <p className="text-gray-500 text-sm">A (best) to E, worst across projects</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Open Vulnerabilities</h3>
            <div className={`text-3xl font-bold ${summary.openVulnerabilities.total ? 'text-brand-red' : 'text-scarf-green'}`}>
              {summary.openVulnerabilities.total}
            </div>
            <p className="text-gray-500 text-sm">
              {SEVERITIES.map((severity) => `${summary.openVulnerabilities[severity]} ${severity}`).join(' · ')}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Hotspots to Review</h3>
            <div className={`text-3xl font-bold ${summary.hotspotsToReview ? 'text-brand-blue-light' : 'text-scarf-green'}`}>
              {summary.hotspotsToReview}
            </div>
            <p className="text-gray-500 text-sm">Code to check by hand</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Last Analysis</h3>
            <div className="text-lg font-bold text-brand-blue-light">
              {summary.lastAnalysis ? new Date(summary.lastAnalysis).toLocaleString() : 'Never'}
            </div>
            <p className="text-gray-500 text-sm">
              {summary.projects.length} project{summary.projects.length === 1 ? '' : 's'} in SonarQube
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by severity">
            {(['all', ...SEVERITIES] as const).map((severity) => (
              <button
                key={severity}
                onClick={() => setSelectedSeverity(severity)}
                aria-pressed={selectedSeverity === severity}
                className={`px-4 py-2 rounded-xl font-bold transition-colors ${
                  selectedSeverity === severity
                    ? 'bg-brand-blue text-white'
                    : 'bg-frost-white text-gray-600 hover:text-deep-navy hover:bg-ice-teal-light'
                }`}
              >
                {severity.charAt(0).toUpperCase() + severity.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={selectedKind}
              onChange={(e) => setSelectedKind(e.target.value as FindingKind | 'all')}
              aria-label="Filter by kind"
              className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
            >
              <option value="all">Vulnerabilities and hotspots</option>
              <option value="vulnerability">Vulnerabilities only</option>
              <option value="hotspot">Hotspots only</option>
            </select>
            <button
              onClick={fetchSecurityData}
              disabled={isLoading}
              className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-6 py-2 rounded-xl transition-colors"
            >
              {isLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {!error && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Findings</h3>
          {visibleFindings.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-lg">
              {findings.length === 0
                ? 'No open vulnerabilities or hotspots to review'
                : 'Nothing matches these filters'}
            </div>
          ) : (
            <ul className="space-y-4">
              {visibleFindings.map((finding) => (
                <li key={finding.id} className="bg-frost-white rounded-xl p-4 border border-ice-teal-light">
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                    <h4 className="font-bold text-deep-navy">{finding.title}</h4>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${SEVERITY_STYLES[finding.severity]}`}>
                        {finding.severity}
                      </span>
                      <span className="px-2 py-1 rounded text-xs font-bold bg-gray-100 text-gray-700">
                        {KIND_LABELS[finding.kind]}
                      </span>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Where:</span>
                      <span className="text-gray-800 ml-2 font-mono break-all">
                        {finding.file}{finding.line ? `:${finding.line}` : ''}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Project:</span>
                      <span className="text-gray-800 ml-2 break-all">{finding.project}</span>
                    </div>
                    {finding.rule && (
                      <div>
                        <span className="text-gray-500">Rule:</span>
                        <span className="text-gray-800 ml-2 font-mono">{finding.rule}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Found:</span>
                      <span className="text-gray-800 ml-2">{new Date(finding.discovered).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <a
                    href={finding.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm font-bold text-brand-blue hover:underline"
                  >
                    Open in SonarQube
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityComponent;
